import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Loader2, CheckCircle2, Users2, Hourglass, PartyPopper } from "lucide-react";
import { walkinApi } from "../../lib/api";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../../lib/branding";

const ACCENT_DEFAULT = "#C0392B";

function sucursalDeUrl(): number | undefined {
  const p = new URLSearchParams(window.location.search).get("sucursal");
  const n = Number(p);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

interface SucConfig {
  logoUrl?: string | null;
  imagenFondoUrl?: string | null;
  colorPrimario?: string | null;
}

export default function WalkinPublicPage() {
  const sucursalId = sucursalDeUrl();
  const [sucNombre, setSucNombre] = useState("");
  const [config, setConfig] = useState<SucConfig | null>(null);

  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [personas, setPersonas] = useState(2);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [entradaId, setEntradaId] = useState<number | null>(null);
  const [estado, setEstado] = useState<"esperando" | "llamado" | "sentado" | "no_show" | "cancelado" | null>(null);
  const [posicion, setPosicion] = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!sucursalId) return;
    fetch("/api/v1/sucursales")
      .then((r) => r.json())
      .then((d: any[]) => {
        const s = Array.isArray(d) ? d.find((x) => x.id === sucursalId) : null;
        if (s?.nombre) { setSucNombre(s.nombre); aplicarTituloPagina(s.nombre); }
      })
      .catch(() => {});
    fetch(`/api/v1/sucursales/${sucursalId}/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setConfig(d); aplicarFaviconPagina(d.logoUrl); } })
      .catch(() => {});
  }, [sucursalId]);

  // Mientras está "esperando" o "llamado", consultar la posición cada 15s.
  useEffect(() => {
    if (!entradaId || (estado !== "esperando" && estado !== "llamado")) return;
    pollRef.current = setInterval(() => {
      walkinApi.status(entradaId)
        .then((r) => { setEstado(r.estado); setPosicion(r.posicion); })
        .catch(() => {});
    }, 15_000);
    return () => clearInterval(pollRef.current);
  }, [entradaId, estado]);

  const accent = config?.colorPrimario || ACCENT_DEFAULT;
  const fondoEstilo: CSSProperties = config?.imagenFondoUrl
    ? {
        backgroundImage: `linear-gradient(rgba(246,241,231,0.88), rgba(246,241,231,0.88)), url(${config.imagenFondoUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : {};

  async function anotarse() {
    setError(null);
    if (!nombre.trim()) { setError("Ingresá tu nombre"); return; }
    if (!telefono.trim()) { setError("Ingresá tu teléfono para avisarte por WhatsApp"); return; }
    if (!sucursalId) { setError("Falta indicar la sucursal (escaneá el QR de la puerta)"); return; }
    setEnviando(true);
    try {
      const r = await walkinApi.crear({
        sucursalId, nombre: nombre.trim(), telefono: telefono.trim(), personas,
      });
      setEntradaId(r.id);
      setPosicion(r.posicion);
      setEstado("esperando");
    } catch (e: any) {
      setError(e.message || "No pudimos anotarte, intentá de nuevo");
    } finally {
      setEnviando(false);
    }
  }

  if (!sucursalId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-ink-50">
        <div className="card max-w-sm w-full p-6 text-center">
          <p className="text-sm text-ink-600">Enlace inválido: falta indicar la sucursal. Pedile al personal que te muestre el QR correcto.</p>
        </div>
      </div>
    );
  }

  // ── Ya se anotó: pantalla de estado en vivo ──
  if (entradaId) {
    return (
      <div className="min-h-screen bg-ink-50 py-10 px-4 flex items-center justify-center" style={fondoEstilo}>
        <div className="max-w-sm w-full card p-8 text-center space-y-4">
          {estado === "llamado" ? (
            <>
              <PartyPopper className="size-14 mx-auto" style={{ color: accent }} />
              <h1 className="font-display font-bold text-xl text-ink-800">¡Tu mesa está lista!</h1>
              <p className="text-sm text-ink-500">Pasá a la entrada, te estamos esperando 🎉</p>
            </>
          ) : estado === "sentado" ? (
            <>
              <CheckCircle2 className="size-14 mx-auto text-emerald-500" />
              <h1 className="font-display font-bold text-xl text-ink-800">¡Disfrutá tu visita!</h1>
            </>
          ) : estado === "no_show" || estado === "cancelado" ? (
            <>
              <Hourglass className="size-14 mx-auto text-ink-300" />
              <h1 className="font-display font-bold text-xl text-ink-800">Tu turno ya no está activo</h1>
              <p className="text-sm text-ink-500">Si seguís acá, pedile al personal que te vuelva a anotar.</p>
            </>
          ) : (
            <>
              <Hourglass className="size-14 mx-auto animate-pulse" style={{ color: accent }} />
              <h1 className="font-display font-bold text-xl text-ink-800">Sos el #{posicion}</h1>
              <p className="text-sm text-ink-500">
                Quedaste anotado en la fila de {sucNombre || "la sucursal"}. Te avisamos por WhatsApp
                apenas tengamos tu mesa lista — no hace falta que te quedes mirando esta pantalla.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Formulario de alta ──
  return (
    <div className="min-h-screen bg-ink-50 py-10 px-4" style={fondoEstilo}>
      <div className="max-w-sm mx-auto card p-6 space-y-5">
        <header className="text-center">
          {config?.logoUrl ? (
            <img src={config.logoUrl} alt="" className="size-14 rounded-full object-cover mx-auto mb-2 border-2" style={{ borderColor: accent }} />
          ) : (
            <Users2 className="size-8 mx-auto mb-2" style={{ color: accent }} />
          )}
          <h1 className="font-display font-bold text-xl text-ink-800">Lista de espera</h1>
          <p className="text-sm text-ink-500 mt-1">{sucNombre || "Anotate y te avisamos por WhatsApp"}</p>
        </header>

        {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">Tu nombre</label>
            <input className="input w-full" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre completo" />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">Teléfono (WhatsApp)</label>
            <input className="input w-full" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="0412-1234567" />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">¿Cuántas personas?</label>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setPersonas((p) => Math.max(1, p - 1))}
                className="w-10 h-10 rounded-lg border border-ink-200 text-ink-600 text-lg font-bold">−</button>
              <span className="text-lg font-semibold text-ink-800 w-8 text-center">{personas}</span>
              <button type="button" onClick={() => setPersonas((p) => Math.min(20, p + 1))}
                className="w-10 h-10 rounded-lg border border-ink-200 text-ink-600 text-lg font-bold">+</button>
            </div>
          </div>
        </div>

        <button onClick={anotarse} disabled={enviando} style={{ backgroundColor: accent }}
          className="w-full py-3 rounded-lg hover:opacity-90 text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {enviando && <Loader2 className="size-4 animate-spin" />}
          Anotarme en la fila
        </button>
      </div>
    </div>
  );
}
